import React, { useEffect, useState } from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import {
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
 } from '@mui/material';
import { fetchUserById } from "../../../redux/actions/users.actions";
import { assignRole, fetchRoles } from "../../../redux/actions/roles.actions";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import { canSelectUser } from "../../../components/common/Interfaces/UserRoleManagement";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";
import { clearError as clearUserError } from "../../../redux/reducers/usersById.reducer";
import { clearError as clearRolesError } from "../../../redux/reducers/roles.reducer";

const EditRoleModal: React.FC<ModalProps> = ({open, setOpen, object_id}) => {
  const user_id = object_id??null;

  const dispatch: AppDispatch = useDispatch();
  const {loading, error, data:user} = useSelector((state: RootState) => state.usersById);
  const {data:roles, loading: rolesLoading, error: rolesError} = useSelector((state: RootState) => state.roles);
  const {data:currentUser} = useSelector((state: RootState) => state.user);
  
  const [submitted, setSubmitted] = useState(false);
  const [role, setRole] = useState<any>(null);
  const [hasShownWarning, setHasShownWarning] = useState(false);

  const canAssignDeveloper = usePermissionCheck(['assign_developer_role']).canAccess;
  const canAssignAdmin = usePermissionCheck(['assign_admin_role']).canAccess;
  const canAssignCorporate = usePermissionCheck(['assign_corporate_role']).canAccess;
  const canAssignOwner = usePermissionCheck(['assign_owner_role']).canAccess;
  const canAssignDealer = usePermissionCheck(['assign_dealer_role']).canAccess;
  const canAssignStaff = usePermissionCheck(['assign_technician_role', 'assign_sales_role', 'assign_customer_role']).canAccess;

  const handleClose = () => {
    dispatch(clearUserError());
    dispatch(clearRolesError());
    setSubmitted(false);
    setHasShownWarning(false);
    setRole(null);
    setOpen(false)
  };

  useEffect(() => {
    if (open) {
      if (user_id && user_id!==user?.id) {
        dispatch(fetchUserById(user_id));
      }

      if (!roles) {
        dispatch(fetchRoles(''));
      }
    }
  }, [dispatch, user_id, open, user, roles]);

  const handleSave = async () => {
    setSubmitted(true);

    if (role) {
      await dispatch(assignRole({role_id:role,users:[user]}))
    }
  }

  const sortRoles = () => {
    let new_array = [...roles];
    const sorted_array = ['developer','admin','corporate','owner','dealer','sales','technician','customer'];

    new_array.sort((a, b) => {
      return sorted_array.indexOf(a.name) - sorted_array.indexOf(b.name);
    });
    return new_array;
  }

  const handleRoleChange = (newRole: string) => {
    const isCorporateUser = user?.roles[0]?.name === 'corporate';
    if (isCorporateUser && newRole !== user?.roles[0]?.id && !hasShownWarning) {
      if (window.confirm('Warning: Changing this user\'s role will unlink them as a corporation from all their dealerships. Do you want to continue?')) {
        setRole(newRole);
        setHasShownWarning(true);
      }
    } else {
      setRole(newRole);
    }
  };

  const isLoading = loading || rolesLoading;
  const failed = Boolean(error || rolesError) && submitted;
  const succeeded = !(error || rolesError) && submitted;
  // Only evaluated once roles have loaded (roles is null before the first fetch)
  const canEditRole = () => [canAssignDeveloper, canAssignAdmin, canAssignCorporate, canAssignOwner,canAssignDealer,canAssignStaff].includes(true) && roles.length>0 && canSelectUser(currentUser?.roles ? currentUser?.roles[0]?.name : null, user?.roles ? user?.roles[0]?.name : null, currentUser?.id, user?.id);

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={isLoading || submitted ? "sm" : "md"}
      tone={isLoading ? undefined : failed ? "error" : succeeded ? "success" : undefined}
      title={isLoading ? "Loading…" : failed ? "An error occurred" : succeeded ? "Role updated" : <>Edit role for <strong>{user?.name}</strong></>}
      actions={isLoading ? undefined : submitted ?
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
        :
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading || rolesLoading || !role}>Save</button>
        </>
      }
    >
      {isLoading ?
        <ModalLoading/>
        : failed ?
        <p>{rolesError ?? error}</p>
        : succeeded ?
        <p>User role was successfully updated.</p>
        : canEditRole() &&
        <div className="dlg-section">
          <span className="dlg-label" id="role-select-label">Role</span>
          <FormControl fullWidth>
            <Select
                labelId="role-select-label"
                id="role-select"
                value={role ?? (user?.roles ? user?.roles[0]?.id : "")}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={rolesLoading}
            >
              {rolesLoading ? (
                <MenuItem value=""><CircularProgress size={"24px"} /></MenuItem>
              ) : (
                sortRoles().map((role: any) =>
                  ( ((role?.name === 'developer') && canAssignDeveloper)
                    || ((role?.name === 'admin') && canAssignAdmin)
                    || ((role?.name === 'corporate') && canAssignCorporate)
                    || ((role?.name === 'owner') && canAssignOwner)
                    || ((role?.name === 'dealer') && canAssignDealer)
                    || ((role?.name === 'sales' || role?.name === 'technician' || (role?.name === 'customer')) && canAssignStaff)
                  ) && (
                    <MenuItem key={role?.id} value={role?.id}>{role?.name}</MenuItem>
                  )
                )
              )}
            </Select>
          </FormControl>
        </div>
      }
    </ModalShell>
  );
};

export default EditRoleModal;
